// src/hooks/useDubbingJobs.js 
import { useState, useEffect, useCallback, useRef } from "react";
import apiService from "../services/apiService";

export const useDubbingJobs = () => {
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  const pollingIntervalRef = useRef(null);
  const mountedRef = useRef(true);

  const getActiveJobStatuses = () => [
    "pending_approval", 
    "approved", 
    "pending", 
    "processing"
  ];

  // Load initial data - ALWAYS fetch ALL jobs
  const loadInitialData = useCallback(async (showLoader = true) => {
    if (!mountedRef.current) return;
    
    try {
      if (showLoader) setLoading(true);
      setError(null);

      // Load jobs and stats in parallel
      const [jobsResponse, statsResponse] = await Promise.all([
        apiService.listDubbingJobs(1, 50, null), 
        apiService.getUserDubbingStats(),
      ]);

      if (mountedRef.current) {
        setAllJobs(jobsResponse.jobs || []);
        setStats(statsResponse);
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      if (mountedRef.current) {
        setError(err.message || "Failed to load dubbing jobs");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  //Improved update job status function
  const updateJobStatus = useCallback(async (jobId) => {
    if (!mountedRef.current) return null;
    
    try {
      const jobStatus = await apiService.getJobStatus(jobId);

      if (mountedRef.current) {
        setAllJobs((prevJobs) =>
          prevJobs.map((job) => {
            if (job.job_id === jobId) {
              const updatedJob = {
                ...job,
                ...jobStatus,
                project_name: job.project_name || jobStatus.project_name,
              };

              // Log status changes for debugging
              if (job.status !== jobStatus.status) {
                console.log(`🔄 Job ${jobId} status: ${job.status} → ${jobStatus.status}`);
              }

              return updatedJob;
            }
            return job;
          })
        );
      }

      return jobStatus;
    } catch (err) {
      console.error(`Error updating job ${jobId}:`, err);
      return null;
    }
  }, []);

  // Manual stop polling function
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      console.log('🛑 Stopping polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  //Completely rewritten polling logic
  useEffect(() => {
    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Get active job statuses
    const activeStatuses = getActiveJobStatuses();
    
    // Filter jobs that need polling
    const activeJobs = allJobs.filter(job => 
      activeStatuses.includes(job.status)
    );

    if (activeJobs.length > 0) {
      console.log(`🔄 Starting polling for ${activeJobs.length} active jobs:`, 
        activeJobs.map(job => `${job.job_id.slice(-6)}(${job.status})`).join(', ')
      );
      
      //Improved polling function that avoids state closure issues
      const pollActiveJobs = async () => {
        if (!mountedRef.current) return;
                
        // Get current jobs fresh from state to avoid stale closures
        setAllJobs((currentJobs) => {
          // Find currently active jobs
          const currentActiveJobs = currentJobs.filter(job => 
            activeStatuses.includes(job.status)
          );

          // Update each active job asynchronously
          currentActiveJobs.forEach(async (job) => {
            if (!mountedRef.current) return;
            
            try {
              console.log(`Checking status for job ${job.job_id.slice(-6)} (current: ${job.status})`);
              const jobStatus = await apiService.getJobStatus(job.job_id);
              
              if (!mountedRef.current) return;
              
              // Log status transitions
              if (jobStatus.status !== job.status) {
                console.log(`📈 Job ${job.job_id.slice(-6)} status changed: ${job.status} → ${jobStatus.status}`);
              }
              
              // Update the specific job in state
              setAllJobs((prevJobs) =>
                prevJobs.map((prevJob) => {
                  if (prevJob.job_id === job.job_id) {
                    return {
                      ...prevJob,
                      ...jobStatus,
                      project_name: prevJob.project_name || jobStatus.project_name,
                    };
                  }
                  return prevJob;
                })
              );
            } catch (err) {
              console.error(`Error updating job ${job.job_id.slice(-6)}:`, err);
            }
          });

          // Return current jobs unchanged 
          return currentJobs;
        });
      };

      // Start polling immediately
      pollActiveJobs();
      
      // Set up interval for continued polling
      pollingIntervalRef.current = setInterval(pollActiveJobs, 5000); // Poll every 5 seconds
    } else {
      console.log('✅ No active jobs, polling stopped');
    }

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    //Better dependency array that tracks job count and active statuses
    allJobs.length,
    allJobs.filter(job => getActiveJobStatuses().includes(job.status)).length,
    // Also track when any job's status changes by creating a hash of active job statuses
    allJobs
      .filter(job => getActiveJobStatuses().includes(job.status))
      .map(job => `${job.job_id}-${job.status}`)
      .sort()
      .join('|')
  ]);

  //Component unmount cleanup
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Initial data load
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Job actions with improved error handling
  const addJob = useCallback((newJob) => {
    if (!mountedRef.current) return;
    
    setAllJobs((prevJobs) => [newJob, ...prevJobs]);

    if (stats) {
      setStats((prev) => ({
        ...prev,
        total_jobs: prev.total_jobs + 1,
        pending_jobs: prev.pending_jobs + 1,
        total_usage_count: prev.total_usage_count + 1,
        remaining_usage:
          prev.remaining_usage !== null ? prev.remaining_usage - 1 : null,
      }));
    }
  }, [stats]);

  const updateJob = useCallback((jobId, updates) => {
    if (!mountedRef.current) return;
    
    setAllJobs((prevJobs) =>
      prevJobs.map((job) =>
        job.job_id === jobId ? { ...job, ...updates } : job
      )
    );
  }, []);

  const removeJob = useCallback((jobId) => {
    if (!mountedRef.current) return;
    
    const jobToRemove = allJobs.find((job) => job.job_id === jobId);

    setAllJobs((prevJobs) => prevJobs.filter((job) => job.job_id !== jobId));

    if (stats && jobToRemove) {
      setStats((prev) => ({
        ...prev,
        total_jobs: Math.max(0, prev.total_jobs - 1),
        [jobToRemove.status + "_jobs"]: Math.max(
          0,
          prev[jobToRemove.status + "_jobs"] - 1
        ),
      }));
    }
  }, [allJobs, stats]);

  // Filter jobs by status
  const getFilteredJobs = useCallback((filter) => {
    if (!filter) return allJobs;
    return allJobs.filter((job) => job.status === filter);
  }, [allJobs]);

  const activeJobsCount = stats?.active_jobs !== undefined 
    ? stats.active_jobs 
    : allJobs.filter(job => 
        getActiveJobStatuses().includes(job.status)
      ).length;

  const refreshJobs = useCallback(async () => {
    await loadInitialData(false);
  }, [loadInitialData]);

  return {
    // Data
    allJobs,
    stats,
    loading,
    error,
    activeJobsCount,

    // Actions
    loadInitialData,
    addJob,
    updateJob,
    removeJob,
    getFilteredJobs,
    refreshJobs, 

    // Utils
    stopPolling,
    updateJobStatus, 
  };
};